<svelte:options customElement={{ tag: "bt-plus-player", shadow: "none", }} />

<script>
  import "vidstack/styles/defaults.css";

  import Hls from "hls.js";
  import mux from "mux-embed";
  import { onMount } from "svelte";
  import { defineCustomElements } from "vidstack/elements";
  import Checkbox from "$shared/checkbox/Checkbox.svelte";
  import { eventBus } from "$shared/lib/eventBus";
  import { debounce } from "$shared/lib/debounce";

  import "vidstack/icons";

  const muxEnvironments = {
    development: "e74bakvfgbrk5mjnc4vh50n7t",
    production: "6m6usp1v0e7c06jh6v1obogtg",
  };
  const muxPlayerInitTime = mux.utils.now();
  let domains = {};
  // If the visitor is coming from China, we need to use different CDNs in prod to avoid the domains blocked by the Great Firewall
  if (window.location.host.includes("bigthinkplus.cn") || window.location.host.includes("plus-web-cn.onrender.com")) {
    domains = {
      development: ["https://v1.dev.bigthink.com", "https://v2.dev.bigthink.com"],
      production: ["https://v1.bigthinkplus.cn", "https://v2.bigthinkplus.cn"]
    };
  } else {
    domains = {
      development: ["https://v1.dev.bigthink.com", "https://v2.dev.bigthink.com"],
      production: ["https://v1.bigthink.com", "https://v2.bigthink.com"],
    };
  }

  let player;
  let playerTries = 0;

  export let debug = false;
  export let env = "production";
  export let poster = null;
  export let sliderVideo = null;
  export let source = null;
  export let thumbnails = null;
  export let ftmMediaID = null;
  export let playerAutoAdvance = null;
  export let playerAutoStart = null;
  export let playerVolume = 0.5;
  export let playerRate = 1;
  export let playerCaptionsEnabled = null;
  export let playerCaptionsLanguage = null;
  export let autoplay = null;
  export let userPresent = false;
  export let nextData = null;
  export let shouldAnimate = false;
  export let upNextText = '';
  export let cancelText = '';
  export let startNowText = '';
  export let upNextTimeout = null;
  export let forceDisableAutoStart = false;
  export let splashPageVideo = false;

  let volumeInstance;
  let rateMenu;
  let rates;

  playerAutoAdvance = coerceStringToBoolean(playerAutoAdvance);
  playerAutoStart = coerceStringToBoolean(playerAutoStart);
  playerCaptionsEnabled = coerceStringToBoolean(playerCaptionsEnabled);

  if (forceDisableAutoStart) {
    playerAutoStart = false;
  }

  // Conditionally enable autoplay, except Safari which breaks
  if (playerAutoStart && !isSafari()) {
    autoplay = true;
  }

  const updateUserPlayerSettings = (payload) => {
    if (userPresent) {
      fetch(`/user_preferences/update`, {
        method: 'PATCH',
        credentials: 'same-origin',
        // @ts-ignore
        headers: {
          'Content-Type': 'application/json',
          // @ts-ignore
          'X-CSRF-Token': document
            .querySelector('meta[name="csrf-token"]')
            .getAttribute('content')
        },
        body: payload
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`PlusPlayer HTTP error! Status: ${response?.status}`);
        }
      })
    }
  }

  const handleVolumeChange = (newVolume) => {
    updateUserPlayerSettings(JSON.stringify({
      player_volume: newVolume
    }))
  }

  const handleRateChange = (newRate) => {
    updateUserPlayerSettings(JSON.stringify({
      player_playback_speed: newRate
    }))
  }

  const handleAutoplayChange = () => {
    updateUserPlayerSettings(JSON.stringify({
      player_auto_advance: playerAutoAdvance
    }))
  }

  const handleCaptionsLanguageChange = (newLanguageValue) => {
    updateUserPlayerSettings(JSON.stringify({
      player_captions_language: newLanguageValue
    }))
  }

  const handleCaptionsEnabledChange = (newEnabledValue) => {
    if (newEnabledValue != true) {
      newEnabledValue = false;
    }
    updateUserPlayerSettings(JSON.stringify({
      player_captions_enabled: newEnabledValue
    }))
  }

  function isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  function coerceStringToBoolean(aString) {
    if (aString == "true" || aString === true) {
      return true;
    } else {
      return false;
    }
  }

  async function fetchData(url) {
    const response = await fetch(url);
    const jsonData = await response.json();
    return jsonData;
  }

  async function checkUrl(url) {
    try {
      const response = await fetch(url, { method: "HEAD" });
      return response.ok ? url : null;
    } catch {
      return null;
    }
  }

  async function findWorkingUrl(staticPath) {
    const host = domains;
    const urlChecks = host[env].map((domain) => checkUrl(domain + staticPath));
    try {
      return await Promise.race(
        urlChecks.map((promise) =>
          promise.then((url) => url && Promise.reject(url))
        )
      );
    } catch (url) {
      return url;
    }
  }

  async function getPoster(ftmMediaID) {
    if (!poster) {
      poster = await findWorkingUrl(`/${ftmMediaID}.poster.jpg`);
    }
  }

  async function getSliderVideo(ftmMediaID) {
    if (!sliderVideo) {
      sliderVideo = await findWorkingUrl(`/${ftmMediaID}.thumbs.mp4`);
    }
  }

  async function getSource(ftmMediaID) {
    if (!source) {
      source = await findWorkingUrl(`/${ftmMediaID}.m3u8`);
    }
  }

  async function getTextTracks(ftmMediaID, outlet) {
    const staticPath = `/${ftmMediaID}.subtitles.json`;
    const url = await findWorkingUrl(staticPath);
    if (url) {
      const domain = url.split(staticPath)[0];
      const subtitles = await fetchData(url);
      for (const subtitle of subtitles) {
        const track = document.createElement("track");
        track.id = subtitle.language;
        track.kind = subtitle.kind;
        track.label = subtitle.label;
        track.srclang = subtitle.language;
        track.src = `${domain}/${subtitle.src}`;
        track.setAttribute("data-type", subtitle.track_type);
        outlet.appendChild(track);
      }
    }
  }

  async function waitForPlayer() {
    playerTries++;
    if (playerTries >= 300) {
      console.error("Player never initialized");
      return;
    }

    if (!player) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      return waitForPlayer();
    }
    return;
  }

  function showUpNextPlayerModal() {
    player.querySelectorAll('.controls').forEach(el => el.classList.add('hidden'));
    player.querySelector('.upnext').classList.remove('hidden')
    setTimeout(() => {
      player.querySelector('.upnext').classList.add('animate')
    }, 200)
    upNextTimeout = setTimeout(() => {
      window.location.href = nextData.URL;
    }, 5200)
  }

  function cancelNext() {
    player.querySelector('.upnext').classList.remove('animate');
    player.querySelector('.upnext').classList.add('hidden');
    player.querySelectorAll('.controls').forEach(el => el.classList.remove('hidden'));
    clearTimeout(upNextTimeout);
  }

  function handleCancelClick() {
    cancelNext();
  }

  function handleStartClick() {
    window.location.href = nextData.URL;
  }

  onMount(async () => {
    await defineCustomElements();
    await waitForPlayer();

    const outlet = player.querySelector("media-outlet");

    if (ftmMediaID) {
      await Promise.all([
        getPoster(ftmMediaID),
        getSliderVideo(ftmMediaID),
        getSource(ftmMediaID),
        getTextTracks(ftmMediaID, outlet),
      ]);
    }

    player.playbackRate = playerRate;
    rates = rateMenu.querySelector('media-playback-rate-menu-items media-radio-group').component.values
    let rateMenuButton = rateMenu.querySelector('media-playback-rate-menu-button div');
    let rateMenuItems = rateMenu.querySelector('media-playback-rate-menu-items media-radio-group');
    rateMenuItems.value = rates.find(rate => rate == +playerRate);
    rateMenuButton.innerText = rateMenuItems.value == 1 ? 'Normal' : `${rateMenuItems.value}x`

    eventBus.on('autoplay-next', event => {
      nextData = event.nextData;

      eventBus.emit('autoplay-next-recieved', {
        nextReceived: true
      });
	  });

    player.addEventListener("end", (event) => {
      if (playerAutoAdvance && nextData?.URL && new URL(nextData.URL).pathname !== '/') {
        showUpNextPlayerModal(nextData);
        document.addEventListener('input', (event) => {
          cancelNext();
        });
      }
      if (splashPageVideo) {
        eventBus.emit('plus-player-end', {
          playerElement: player,
          ftmMediaId: ftmMediaID
        });
      }
    });

    player.addEventListener("can-play", (event) => {
      if (debug) console.log(event);
      const newEvent = new CustomEvent("player-ready", {
        detail: event.detail,
        bubbles: true,
        cancelable: true,
        composed: true, // makes the event jump shadow DOM boundary
      });
      player.dispatchEvent(newEvent);

      eventBus.emit('plus-player-ready', {
        playerElement: player,
        ftmMediaId: ftmMediaID
      })

      if (splashPageVideo) {
        window.amplitude?.track("Splash Player Ready");
      }
    });

    player.addEventListener("play", () => {
      if (splashPageVideo) {
        window.amplitude?.track("Splash Player Play Button Clicked");
      }
    });

    player.addEventListener("hls-error", (event) => {
      if (debug) console.log(event);
      const data = event.detail;
      const hls = event.target.provider.instance;
      if (data.fatal) {
        if (splashPageVideo) {
          window.amplitude?.track("Splash Player Failed", {
            errorType: data.type,
            errorDetails: data.details,
          });
        }
        switch (data.type) {
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log("fatal media error encountered, trying to recover");
            hls.recoverMediaError();
            break;
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.error("fatal network error encountered", data);
            // All retries and media options have been exhausted.
            // Immediately trying to restart loading could cause loop loading.
            // Consider modifying loading policies to best fit your asset and network
            // conditions (manifestLoadPolicy, playlistLoadPolicy, fragLoadPolicy).
            break;
          default:
            // cannot recover
            console.log("cannot recover");
            eventBus.emit('plus-player-failed', {
              playerElement: player,
              ftmMediaId: ftmMediaID
            });
            hls.destroy();
            break;
        }
      }
    });

    player.addEventListener("provider-change", (event) => {
      if (debug) console.log(event);
      const provider = event.detail;
      if (provider?.type === "hls") {
        provider.library = Hls;
        provider.config = {
          debug: debug,
          manifestLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 3000,
              maxLoadTimeMs: 10000,
              timeoutRetry: {
                maxNumRetry: 1,
                retryDelayMs: 500,
                maxRetryDelayMs: 6000,
              },
              errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 500,
                maxRetryDelayMs: 6000,
              },
            },
          },
          playlistLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 3000,
              maxLoadTimeMs: 7000,
              timeoutRetry: {
                maxNumRetry: 1,
                retryDelayMs: 500,
                maxRetryDelayMs: 6000,
              },
              errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 500,
                maxRetryDelayMs: 6000,
              },
            },
          },
          fragLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 3000,
              maxLoadTimeMs: 7000,
              timeoutRetry: {
                maxNumRetry: 1,
                retryDelayMs: 500,
                maxRetryDelayMs: 6000,
              },
              errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 500,
                maxRetryDelayMs: 6000,
              },
            },
          },
        };
      }
    });

    player.addEventListener("provider-setup", (event) => {
      if (debug) console.log(event);
      const provider = event.detail;
      if (provider?.type === "hls") {
        mux.monitor(provider.video, {
          debug: debug,
          // don't set the muxData cookie (flagged in pen test: not HttpOnly/Secure)
          disableCookies: true,
          hlsjs: provider.instance,
          Hls: Hls,
          data: {
            env_key: muxEnvironments[env],
            // Player metadata
            player_name: "FTMMM Player", // any arbitrary string you want to use to identify this player
            player_init_time: muxPlayerInitTime, // ex: 1451606400000
            // Video metadata
            video_id: ftmMediaID,
            video_title: ftmMediaID,
          },
        });
      }
    });

    volumeInstance.addEventListener("media-volume-change-request", debounce((event => {
      handleVolumeChange(event.detail);
    }), 200));

    player.addEventListener("media-rate-change-request", debounce((event => {
      // Special handling for edge case when moving from non-1 to 1 after loading with non-1
      if (event.detail != player.playbackRate) {
        player.playbackRate = event.detail;
        let rateMenuButton = rateMenu.querySelector('media-playback-rate-menu-button div');
        rateMenuButton.innerText = rateMenuItems . value == 1 ? 'Normal' : `${rateMenuItems.value}x`
      }
      handleRateChange(event.detail);
    }), 200));

    player.addEventListener("text-track-change", event => {
      let newTrack = event.detail;
      let newLanguage = newTrack?.language;
      let newEnabled = newTrack && newTrack.mode === 'showing';
      if (newLanguage) {
        handleCaptionsLanguageChange(newLanguage);
      }
      handleCaptionsEnabledChange(newEnabled);
    });
  });
</script>


<!-- shadow-lg if not splash -->
<media-player
  class={`overflow-hidden aspect-video w-full ${splashPageVideo ? '' : 'shadow-lg'}`}
  src={source}
  {autoplay}
  {poster}
  {thumbnails}
  aspect-ratio="16/9"
  volume={playerVolume}
  playbackRate={+playerRate}
  playsinline
  bind:this={player}
>
  <media-outlet>
    <media-poster />
    <slot />
    <media-gesture
      class="top-0 left-0 h-full w-full"
      event="pointerup"
      action="toggle:paused"
    />
    <media-gesture
      class="top-0 left-0 h-full w-full"
      event="dblpointerup"
      action="toggle:fullscreen"
    />
    <media-gesture
      class="top-0 left-0 z-10 h-full w-1/5"
      event="dblpointerup"
      action="seek:-10"
    />
    <media-gesture
      class="top-0 right-0 z-10 h-full w-1/5"
      event="dblpointerup"
      action="seek:10"
    />
  </media-outlet>
  <div
    class="z-0 bg-black/30 transition-opacity opacity-0 not-user-idle:opacity-100 pointer-events-none bg-blur absolute inset-0 w-full h-full"
  />
  <media-captions
    class="not-user-idle:bottom-[80px] transition-[bottom] duration-300 z-10"
  />
  <media-buffering-indicator />
  <div
    class="can-control:opacity-100 pointer-events-none absolute inset-0 z-10 flex h-full flex-col justify-between text-white opacity-0 transition-opacity duration-200 ease-linear"
  >
    <div
      class="absolute inset-0 z-50 pointer-events-none user-idle:pointer-events-auto user-idle:cursor-none"
    />

    <div
      class="controls flex items-center pointer-events-auto w-fill p2 z-50 mt-2"
    >
      <div class="flex-1" />
      <media-menu position="bottom">
        <media-menu-button>
          <media-icon type="settings" data-rotate />
          <media-tooltip position="bottom right">Settings</media-tooltip>
        </media-menu-button>
        <media-menu-items>
          <media-menu bind:this={rateMenu}>
            <media-playback-rate-menu-button />
            <media-playback-rate-menu-items />
          </media-menu>
          <media-menu>
            <media-captions-menu-button />
            <media-captions-menu-items />
          </media-menu>
        </media-menu-items>
      </media-menu>
    </div>

    <div class="flex flex-1 items-center justify-center">
      <div class="upnext hidden absolute left-0 right-0 top-0 bottom-0 sm:static sm:rounded-lg p-8 {shouldAnimate ? 'animate' : ''}">
        <div class="flex flex-row">
          <div class=" pr-3 pt-3">
            <div class="icon-circle border border-white rounded-full p-2">
              {#if nextData?.klass === "lesson" || nextData?.klass === "learningpathlesson" || nextData?.klass === "video"}
                <bt-icon icon="Play" size="small"></bt-icon>
              {:else if nextData?.klass === "mission"}
                <bt-icon icon="Map" size="small"></bt-icon>
              {:else if nextData?.klass === "discussion"}
                <bt-icon icon="MessagesSquare" size="small"></bt-icon>
              {:else if nextData?.klass === "reflection"}
                <bt-icon icon="EditAlt" size="small"></bt-icon>
              {/if}
            </div>
          </div>
          <div class="min-w-0 sm:min-w-full">
            <div class="uppercase text-neutral-300 text-xs">{upNextText}: {nextData?.type}</div>
            <div class="truncate sm:overflow-visible sm:whitespace-normal sm:text-clip">{nextData?.title}</div>
            <div class="text-neutral-300 text-xs">{nextData?.duration}</div>
          </div>
        </div>
        <div class="flex justify-between pt-8">
          <button on:click={handleCancelClick} id="playerCancel" type="button" class="px-4 py-2 mr-12 hover:cursor-pointer pointer-events-auto hover:underline">{cancelText}</button>
          <button on:click={handleStartClick} id="playerStartNow" type="button" class="px-4 py-2 border border-white rounded-lg bg-yellow text-black hover:cursor-pointer pointer-events-auto hover:border-yellow hover:bg-transparent hover:text-white">{startNowText}</button>
        </div>
      </div>
    </div>
    <div
      class="controls flex items-center pointer-events-auto w-full px-4 -mb-2 z-10"
    >
      <media-time
        class="text-sm tracking-wide mr-1"
        type="current"
      />
      <media-time-slider>
        {#if thumbnails}
          <div slot="preview">
            <div>
              <media-slider-thumbnail class="rounded-sm" />
              <div
                class="absolute bottom-8 w-full text-center"
                part="chapter-title"
              />
            </div>
            <media-slider-value
              class="rounded-sm mt-2"
              type="pointer"
              format="time"
            />
          </div>
        {:else}
          <media-slider-video src={sliderVideo} slot="preview" />
        {/if}
      </media-time-slider>
      <media-time
        class="text-sm tracking-wide ml-1"
        type="current"
        remainder
      />
    </div>
    <div
      class="controls flex items-center pointer-events-auto w-full mb-1 z-20 px-0.5"
    >
      <media-play-button data-amp-default-track default-appearance>
        <media-tooltip position="top left">
          <span slot="play">Play (k)</span>
          <span slot="pause">Pause (k)</span>
        </media-tooltip>
      </media-play-button>
      <media-mute-button class="-ml-1" default-appearance>
        <media-tooltip>
          <span slot="mute">Mute (m)</span>
          <span slot="unmute">Unmute (m)</span>
        </media-tooltip>
      </media-mute-button>
      <media-volume-slider class="max-w-[64px] -ml-1.5" bind:this={volumeInstance}>
        <media-slider-value
          class="rounded-sm"
          type="pointer"
          format="percent"
          slot="preview"
        />
      </media-volume-slider>
      <div class="flex-1 text-center" />
      {#if !splashPageVideo}
        <Checkbox
          design="switch-player"
          label="Auto-advance"
          bind:isChecked={playerAutoAdvance}
          on:checkbox-changed={handleAutoplayChange}
        ></Checkbox>
      {/if}
      <media-caption-button default-appearance>
        <media-tooltip>
          <span slot="on">Closed-Captions On (c)</span>
          <span slot="off">Closed-Captions Off (c)</span>
        </media-tooltip>
      </media-caption-button>
      <media-pip-button default-appearance>
        <media-tooltip>
          <span slot="enter">Enter PIP (i)</span>
          <span slot="exit">Exit PIP (i)</span>
        </media-tooltip>
      </media-pip-button>
      <media-fullscreen-button class="right-2 ml-2" default-appearance>
        <media-tooltip position="top right">
          <span slot="enter">Enter Fullscreen (f)</span>
          <span slot="exit">Exit Fullscreen (f)</span>
        </media-tooltip>
      </media-fullscreen-button>
    </div>
  </div>
</media-player>

<style>
  .upnext {
    pointer-events: auto;
    background: rgb(76, 76, 76);
    background: linear-gradient(to left, rgb(76, 76, 76) 50%, rgb(108, 107, 105) 51%) right;
    background-size: 205%;
    transition: background-position 5s linear;
  }

  .upnext.animate {
    background-position: left;
  }
</style>
